import axios from 'axios';
import { useSnackbar } from 'notistack5';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NotificationData, NotificationRequestData, useNotificationApi } from 'src/api/useNotificationApi';

export const useNotificationEdit = (id?: string) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [data, setData] = useState<NotificationData>();
  const { update, getByIdForAdmin } = useNotificationApi();
  const { create } = useNotificationApi();

  const duplicateNotification = useCallback(
    async (data: NotificationRequestData) => {
      if (!data?.titleEng?.trim() || /^[\s\u3000]+&/.test(data.titleEng)) {
        data.titleEng = data.titleJpn;
      }
      if (!data?.contentEng?.trim() || /^[\s\u3000]+&/.test(data.contentEng)) {
        data.contentEng = data.contentJpn;
      }

      setIsLoading(true);

      try {
        await create(data);
        navigate('/manage/notification/edit');
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status !== 403) {
            enqueueSnackbar(t('notifications.messages.failedToDuplicate'), { variant: 'error' });
          }
        }
        setHasError(true);
        setIsLoading(false);
      }
    },
    [create, enqueueSnackbar, navigate, t],
  );

  const updateNotification = useCallback(
    async (id: string, data: NotificationRequestData) => {
      // memo: 英語の入力がない(空白を含む)場合は日本語の入力を利用する。
      if (!data.titleEng.trim() || /^[\s\u3000]+&/.test(data.titleEng)) {
        data.titleEng = data.titleJpn;
      }
      if (!data.contentEng.trim() || /^[\s\u3000]+&/.test(data.contentEng)) {
        data.contentEng = data.contentJpn;
      }

      setIsLoading(true);

      try {
        await update(id, data);
        navigate('/manage/notification/edit');
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          enqueueSnackbar(t('notifications.messages.failedToUpdate'), { variant: 'error' });
        }
        setHasError(true);
        setIsLoading(false);
      }
    },
    [enqueueSnackbar, navigate, t, update],
  );

  const fetchNotification = useCallback(async () => {
    if (!id || data) return;

    setIsLoading(true);

    try {
      const response = await getByIdForAdmin(id);
      if (!response) throw Error;
      setData(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status !== 403) {
          enqueueSnackbar(t('notifications.messages.failedToRetrieve'), { variant: 'error' });
        }
      }
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [data, enqueueSnackbar, getByIdForAdmin, id, t]);

  useEffect(() => {
    fetchNotification();
  }, [fetchNotification]);

  return { isLoading, hasError, notification: data, duplicateNotification, updateNotification };
};
